import { Icon } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IDataTableColumn, useGridState, useSearchApi } from "nsitools-react";
import * as React from "react";

import {
  DogHealthSearch,
  DogHealthTestGridDto,
  DogsTransientCharacteristicsApi,
  TransientCharacteristicByDogSearch,
} from "../../../../api";
import { useEventsContext, useGlobalData } from "../../../../contexts";
import { useApiService, useTl } from "../../../../hooks";
import { ETLCodes } from "../../../../locales";
import { DogTransientCharacteristicList } from "../../../../pages/dashboard/health/DogTransientCharacteristics";
import { DrupalDataTable } from "../../../common";
import { DrupalFieldSet } from "../../../common/DurpalFieldSet";

export interface IDrupalDogTransientCharacteristicListProps {
  dogId: number;
}

export const DrupalDogTransientCharacteristicList: React.FunctionComponent<IDrupalDogTransientCharacteristicListProps> = ({
  dogId,
}) => {
  const { t } = useTl();
  const api = useApiService(DogsTransientCharacteristicsApi);
  const { currentLanguage } = useGlobalData();

  const tableState = useGridState({
    serverMode: true,
    enablePagination: true,
    enableFilter: true,
    availablePageSizes: [15, 25, 50],
    pageSize: 15,
  });

  const searchFunction = React.useCallback(
    (sObj: DogHealthSearch) => {
      return api.baseSearch(
        TransientCharacteristicByDogSearch.fromJS({
          ...sObj,
          dogId: dogId,
          languageId: currentLanguage.languageId,
        })
      );
    },
    [api, currentLanguage.languageId, dogId]
  );

  const { search, loading } = useSearchApi({
    searchFunction,
    tableState,
    initialSearch: true,
    trimSearchObjectStringValues: true,
  });

  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        header: () => t(ETLCodes.Characteristic),
        fieldName: "characteristic",
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.Value),
        fieldName: "healthValue",
        disableSorting: true,
      },
      {
        header: () => t(ETLCodes.TestType),
        fieldName: "test",
        disableSorting: true,
        render: (row: DogHealthTestGridDto) => {
          if (row?.characteristicType?.includes("03")) {
            return <Icon icon={IconNames.EYE_OPEN} />;
          } else if (row?.characteristicType?.includes("04")) {
            return <Icon icon={IconNames.HEART} />;
          } else if (row?.characteristicType?.includes("09")) {
            return <Icon icon={IconNames.CHART} />;
          }
        },
      },
    ],
    [t]
  );

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RefreshSearchTables", search);
    return () => unsubscribeEvent("RefreshSearchTables", search);
  }, [search, subscribeToEvent, unsubscribeEvent]);

  React.useEffect(() => {
    search();
  }, [currentLanguage, search]);

  return (
    <>
      <DrupalFieldSet title={t(ETLCodes.AwaitingValidation)}>
        <DrupalDataTable tableState={tableState} loading={loading} columns={columns} />
      </DrupalFieldSet>
    </>
  );
};
